import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getFormattedDate, getTimezone } from "common/Utilities";
import EditableTableComponent from "../../components/editable-table/EditableTableComponent";
import { XemelgoService } from "../../services/XemelgoService";
import { CollapsibleListView } from "../../components/collapsible-list-view";
import useSubscriptionConditionParser from "./TimeExceeded/utils/useSubscriptionConditionParser.js";

const TimeExceededRuleContent = ({
  locationTreeMap,
  ruleId,
  ruleConditionsList,
  onLoad: onLoadProp,
  ruleConfig = {}
}) => {
  const [headers, setHeaders] = useState([]);
  const [editedTime, setEditedTime] = useState(undefined);
  const { parse: parseConditionsForSubscription } = useSubscriptionConditionParser();

  const getLocationName = useCallback(
    (locationId) => {
      if (!locationTreeMap[locationId]) {
        return "All Locations";
      }
      let displayName = locationTreeMap[locationId].name;
      let parentLocationId = locationTreeMap[locationId].directParentId;
      while (parentLocationId) {
        displayName = `${locationTreeMap[parentLocationId].name} → ${displayName}`;
        parentLocationId = locationTreeMap[parentLocationId].directParentId;
      }
      return displayName;
    },
    [locationTreeMap]
  );

  const allLocationCondition = useMemo(() => {
    return ruleConditionsList.find((rule) => !rule.tags.locationId);
  }, [ruleConditionsList]);

  const locationRules = useMemo(() => {
    return Object.values(
      ruleConditionsList.reduce((accumulator, eachCondition) => {
        const { locationId, stage, threshold, expeditedWarningThreshold, expeditedCriticalThreshold } =
          eachCondition.tags || {};
        accumulator[locationId] = {
          ...accumulator[locationId],
          subscribed: accumulator[locationId]?.subscribed || eachCondition.hasSubscriptions,
          id: [...(accumulator[locationId]?.id || []), eachCondition.id],
          conditionId: [
            ...(accumulator[locationId]?.conditionId || []),
            { ruleConditionId: eachCondition.id, type: `${stage || "allLocation"}Condition` }
          ],
          locationId,
          locationName: getLocationName(locationId),
          [`${stage}Threshold`]: threshold,
          expeditedCriticalThreshold,
          expeditedWarningThreshold: accumulator[locationId]?.expeditedWarningThreshold || expeditedWarningThreshold,
          disableDelete: !locationId,
          disableEdit: !locationId
        };
        return accumulator;
      }, {})
    ).sort((a, b) => {
      return a.locationName.localeCompare(b.locationName, undefined, {
        numeric: true,
        sensitivity: "base"
      });
    });
  }, [ruleConditionsList, getLocationName]);

  const handleSubscriptionChange = async (ruleConditionId, subscribing) => {
    const RulePageClient = XemelgoService.getClient().getRulePageClient();
    const { subscribeIds, unsubscribeIds } = parseConditionsForSubscription(
      ruleConditionId,
      subscribing,
      locationRules
    );

    await RulePageClient.updateSubscriptionForRuleConditions(subscribeIds, unsubscribeIds);
    await onLoadProp();
  };

  const locationOptions = useMemo(() => {
    return Object.values(locationTreeMap)
      .filter((eachLocation) => {
        return (
          eachLocation.childLocations.length <= 1 &&
          !locationRules.find((each) => {
            return each.locationId === eachLocation.id;
          })
        );
      })
      .map((eachLocation) => {
        const { id } = eachLocation;
        return { objectTypeId: id, objectTypeName: getLocationName(id) };
      })
      .sort((a, b) => {
        return a.objectTypeName.localeCompare(b.objectTypeName, undefined, {
          numeric: true,
          sensitivity: "base"
        });
      });
  }, [getLocationName, locationRules]);

  useEffect(() => {
    const temporaryHeaders = [
      {
        displayName: "Location",
        id: "locationName",
        cell: {
          input: "dropdown",
          data: locationOptions,
          display: "text",
          modifiable: false // whether it can be edited after being added
        }
      },
      {
        displayName: "Warning Threshold",
        id: "warningThreshold",
        cell: {
          input: "text",
          display: "text",
          modifiable: true // whether it can be edited after being added
        }
      },
      {
        displayName: "Critical Threshold",
        id: "criticalThreshold",
        cell: {
          input: "text",
          display: "text",
          modifiable: true // whether it can be edited after being added
        }
      },
      {
        displayName: "Expedited Warning Threshold",
        id: "expeditedWarningThreshold",
        cell: {
          input: "text",
          display: "text",
          modifiable: true // whether it can be edited after being added
        }
      },
      {
        displayName: "Expedited Critical Threshold",
        id: "expeditedCriticalThreshold",
        cell: {
          input: "text",
          display: "text",
          modifiable: true // whether it can be edited after being added
        }
      }
    ];
    if (ruleConfig.publishToIoT) {
      temporaryHeaders.unshift({
        displayName: "",
        id: "subscribed",
        cell: {
          input: "switch",
          display: "switch",
          modifiable: true // whether it can be edited after being added
        }
      });
    }
    setHeaders(temporaryHeaders);
  }, [ruleConfig, locationOptions]);

  const handleValidationFn = (payloads) => {
    const inputErrorMap = {};

    const validatedObject = {
      errorExists: false,
      errorMap: inputErrorMap
    };

    payloads.forEach((payloadItem) => {
      const inputError = {
        warningThreshold: false,
        criticalThreshold: false,
        expeditedWarningThreshold: false,
        expeditedCriticalThreshold: false,
        locationName: false
      };

      const {
        _event,
        warningThreshold,
        criticalThreshold,
        locationName,
        id: currentRowId,
        expeditedWarningThreshold,
        expeditedCriticalThreshold
      } = payloadItem;

      if (_event !== "pending_delete") {
        /**
         * (+thresholdValue === +thresholdValue) is called a 'self check' that is used to check if it is a valid number
         * '+variable' converts the value into a number.
         */
        if (!locationName) {
          inputError.locationName = true;
        }
        if (
          warningThreshold === "" ||
          !warningThreshold ||
          // eslint-disable-next-line no-self-compare
          !(+warningThreshold === +warningThreshold) ||
          +warningThreshold <= 0
        ) {
          inputError.warningThreshold = true;
        }
        if (
          expeditedWarningThreshold === "" ||
          !expeditedWarningThreshold ||
          // eslint-disable-next-line no-self-compare
          !(+expeditedWarningThreshold === +expeditedWarningThreshold) ||
          +expeditedWarningThreshold <= 0
        ) {
          inputError.expeditedWarningThreshold = true;
        }
        if (
          criticalThreshold === "" ||
          !criticalThreshold ||
          // eslint-disable-next-line no-self-compare
          !(+criticalThreshold === +criticalThreshold) ||
          +criticalThreshold <= 0 ||
          +warningThreshold >= +criticalThreshold
        ) {
          inputError.criticalThreshold = true;
        }
        if (
          expeditedCriticalThreshold === "" ||
          !expeditedCriticalThreshold ||
          // eslint-disable-next-line no-self-compare
          !(+expeditedCriticalThreshold === +expeditedCriticalThreshold) ||
          +expeditedCriticalThreshold <= 0 ||
          +expeditedWarningThreshold >= +expeditedCriticalThreshold
        ) {
          inputError.expeditedCriticalThreshold = true;
        }

        inputErrorMap[currentRowId] = inputError;
      }
    });

    const BreakForEachLoop = { exception: "Error exists." };

    try {
      Object.keys(inputErrorMap).forEach((key) => {
        if (
          inputErrorMap[key].warningThreshold ||
          inputErrorMap[key].criticalThreshold ||
          inputErrorMap[key].expeditedWarningThreshold ||
          inputErrorMap[key].expeditedCriticalThreshold ||
          inputErrorMap[key].locationName
        ) {
          validatedObject.errorExists = true;
          throw BreakForEachLoop;
        } else {
          validatedObject.errorExists = false;
        }
      });
    } catch (e) {
      alert("Please enter valid values in the highlighted field(s).");
      if (e !== BreakForEachLoop) {
        throw e;
      }
    }

    validatedObject.errorMap = inputErrorMap;

    return validatedObject;
  };

  const getCreateRuleConditionPayloads = (condition) => {
    return [
      {
        name: `timeExceeded_${condition.locationName.replace(/[^\w\s]/gi, "_")}_${condition.locationId}_warning`,
        tags: {
          warningThreshold: condition.warningThreshold * 1,
          criticalThreshold: condition.criticalThreshold * 1,
          expeditedWarningThreshold: condition.expeditedWarningThreshold * 1,
          expeditedCriticalThreshold: condition.expeditedCriticalThreshold * 1,
          threshold: condition.warningThreshold * 1,
          locationId: condition.locationId,
          locationName: condition.locationName,
          stage: "warning"
        },
        conditions: [
          {
            property: "timeExceededValue",
            op: ">=",
            value: condition.warningThreshold * 1
          },
          {
            property: "timeExceededValue",
            op: "<=",
            value: condition.criticalThreshold * 1
          },
          {
            property: "locationId",
            op: "=",
            value: condition.locationId
          }
        ]
      },
      {
        name: `timeExceeded_${condition.locationName.replace(/[^\w\s]/gi, "_")}_${condition.locationId}_critical`,
        tags: {
          criticalThreshold: condition.criticalThreshold * 1,
          expeditedCriticalThreshold: condition.expeditedCriticalThreshold * 1,
          threshold: condition.criticalThreshold * 1,
          locationId: condition.locationId,
          locationName: condition.locationName,
          stage: "critical"
        },
        conditions: [
          {
            property: "timeExceededValue",
            op: ">",
            value: condition.criticalThreshold * 1
          },
          {
            property: "locationId",
            op: "=",
            value: condition.locationId
          }
        ]
      }
    ];
  };

  const getUpdateRuleConditionPayloads = (requestPayload) => {
    return requestPayload.conditionId.map((eachId) => {
      const tags = {};
      const conditions = [];
      tags.locationId = requestPayload.locationId;
      tags.locationName = requestPayload.locationName;
      if (eachId.type === "warningCondition") {
        tags.stage = "warning";
        tags.warningThreshold = requestPayload.warningThreshold * 1;
        tags.criticalThreshold = requestPayload.criticalThreshold * 1;
        tags.expeditedWarningThreshold = requestPayload.expeditedWarningThreshold * 1;
        tags.expeditedCriticalThreshold = requestPayload.expeditedCriticalThreshold * 1;
        tags.threshold = requestPayload.warningThreshold * 1;
        conditions.push({ property: "timeExceededValue", op: ">=", value: tags.warningThreshold });
        conditions.push({ property: "timeExceededValue", op: "<=", value: tags.criticalThreshold });
      } else {
        tags.stage = "critical";
        tags.threshold = requestPayload.criticalThreshold * 1;
        tags.criticalThreshold = requestPayload.criticalThreshold * 1;
        tags.expeditedCriticalThreshold = requestPayload.expeditedCriticalThreshold * 1;

        conditions.push({ property: "timeExceededValue", op: ">", value: tags.criticalThreshold });
      }
      conditions.push({ property: "locationId", op: "=", value: tags.locationId });

      return {
        id: eachId.ruleConditionId,
        tags,
        conditions
      };
    });
  };

  const handleCancel = () => {
    setEditedTime(undefined);
  };

  const renderSettings = () => {
    const { tags = {} } = allLocationCondition || {};
    const { timeToTrigger = 0 } = tags;
    const date = editedTime !== undefined ? editedTime : getFormattedDate(timeToTrigger, "HH:mm");
    return (
      <div className="alerts-settings-container">
        <div className="alerts-settings-text">
          Notification Time
          <div className="switch-helper-text">When should the notification be sent?</div>
        </div>
        <div className="early-expiration-container">
          <div>
            <input
              className={editedTime === "" ? "time_input input-error" : "time_input"}
              onChange={(event) => {
                setEditedTime(event.target.value);
              }}
              id="notfication-time"
              type="time"
              value={date}
            />
            {`(${getTimezone()})`}
          </div>
          {editedTime === "" && <p className="error-text">Enter a time ex. 12:00 AM</p>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div>
        <p className="tabbedSectionComponent-contentTitle">Time Exceeded</p>
        <p style={{ color: "#343434", paddingBottom: 10 }}>
          Mark orders as "Time Exceeded" if orders stay at the location longer than the set threshold.
        </p>
      </div>
      {ruleConfig.publishToIoT && (
        <CollapsibleListView
          className="alert-switches"
          key="stock"
          title="Alert Settings"
          content={renderSettings()}
          openAtStart
        />
      )}
      <EditableTableComponent
        handleSubscriptionChange={handleSubscriptionChange}
        headers={headers}
        dataList={locationRules}
        handleChangesFn={async (conditions) => {
          const RulePageClient = XemelgoService.getClient().getRulePageClient();
          const promiseList = [];
          conditions.forEach((eachCondition) => {
            switch (eachCondition._event) {
              case "pending_new":
                getCreateRuleConditionPayloads(eachCondition).forEach((eachPayload) => {
                  promiseList.push(
                    RulePageClient.createRuleCondition(
                      eachPayload.name,
                      eachPayload.tags,
                      ruleId,
                      eachPayload.conditions,
                      "no_repeat"
                    )
                  );
                });
                break;
              case "pending_update":
                getUpdateRuleConditionPayloads(eachCondition).forEach((eachPayload) => {
                  promiseList.push(
                    RulePageClient.updateRuleCondition(
                      eachPayload.id,
                      undefined,
                      eachPayload.tags,
                      eachPayload.conditions
                    )
                  );
                });
                break;
              case "pending_delete":
                eachCondition.conditionId.forEach((eachId) => {
                  promiseList.push(RulePageClient.removeRuleCondition(eachId.ruleConditionId, false));
                });
                break;
              default:
                break;
            }
          });
          if (editedTime) {
            const splitTime = editedTime.split(":");
            const date = new Date();
            date.setHours(+splitTime[0], +splitTime[1], 0, 0);
            promiseList.push(
              RulePageClient.updateRuleCondition(allLocationCondition.id, undefined, {
                ...allLocationCondition.tags,
                timeToTrigger: date.getTime()
              })
            );
          }
          await Promise.all(promiseList);
          await onLoadProp();
          return [];
        }}
        hasChanges={!!editedTime}
        handleValidationFn={handleValidationFn}
        handleAutoFillFn={(headerId, newValue, data) => {
          const autoFillObject = {
            data,
            changed: true
          };

          if (headerId === "locationName") {
            const itemIndex = locationOptions
              .map((i) => {
                return i.objectTypeName;
              })
              .indexOf(newValue);
            const itemId = locationOptions[itemIndex].objectTypeId;
            data.locationId = itemId;
          }
          autoFillObject.data = data;

          if (data.name === "" && data.threshold === "") {
            autoFillObject.changed = false;
          }

          return autoFillObject;
        }}
        cancelFunc={handleCancel}
      />
    </div>
  );
};

export default TimeExceededRuleContent;
